import React, { useEffect, useMemo, useState } from "react";
import CircularProgress from "Components/Common/CircularProgress";
import { useJobProgress } from "Components/Hooks/useJobProgress";
import { usePrintPackageJobProgress } from "Components/Hooks/usePrintPackageJobProgress";
import { useQrImageJobProgress } from "Components/Hooks/useQrImageJobProgress";
import { QR_GENERATION_JOB_TERMINAL_EVENT } from "hooks/useCancelQrGenerationJob";
import {
  getStoredActivePrintExportJobs,
  getStoredActiveQrImageExportJobs,
  buildJobPanelKey,
  JOB_PANEL_TOGGLE_REQUEST_EVENT,
  JOB_PANEL_STATE_EVENT,
} from "helpers/activeExportJobsStorage";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('issuance_plan');

interface PlanStatusColumnProps {
  planId: number;
  statusBadgeClassName: string;
  statusLabel: string;
  rejectionReason?: string | null;
}

const getStoredQrGenerationJobId = (planId: number) =>
  localStorage.getItem(`lms_active_job_${planId}`);

// Phat tu ExportJobCard (GlobalJobProgress.tsx) khi buoc "gui nha in" tu dong sau xuat CSV that su
// ket thuc (thanh cong hoac bi huy) — job export CSV tu no bao "completed" som hon nhieu (chi moi
// xong file zip), nen khong the dung rieng trang thai job de biet luc nao an progress bar o day.
const PRINT_EXPORT_JOB_TERMINAL_EVENT = "lms_print_export_job_terminal";
// QR_GENERATION_JOB_TERMINAL_EVENT import tu hooks/useCancelQrGenerationJob.ts (dung chung voi nut
// Huy trong popup tien trinh noi va cot Thao tac cua man Sinh Bo So & QR) — phat ngay khi huy thanh
// cong, KHONG the doi polling useJobProgress ben duoi tu phat hien job chuyen sang "cancelled", vi
// huy giua luc dang "processing" chi bao CancelRequested=true phia backend (QrGenerationJob.
// MarkCancelled() chi chay sau, khi consumer nen dang chay tu phat hien co nay o lan poll tiep theo
// — co the mat vai giay đến vai chuc giay). Neu chi doi status tu job tu chuyen, progress bar o day
// se bi ket "Đang sinh vé/QR..." rat lau du IssuancePlan.Status da duoc backend chuyen sang Cancel
// ngay lap tuc trong cung transaction.

const PlanStatusColumn: React.FC<PlanStatusColumnProps> = ({
  planId,
  statusBadgeClassName,
  statusLabel,
  rejectionReason,
}) => {
  const [qrGenJobId, setQrGenJobId] = useState<string | null>(() =>
    getStoredQrGenerationJobId(planId),
  );
  const [qrGenPercent, setQrGenPercent] = useState(0);

  const printJob = useMemo(
    () =>
      getStoredActivePrintExportJobs().find(
        (j) => j.issuancePlanId === planId,
      ) || null,
    [planId],
  );
  const qrImageJob = useMemo(
    () =>
      getStoredActiveQrImageExportJobs().find(
        (j) => j.issuancePlanId === planId,
      ) || null,
    [planId],
  );

  const [printPercent, setPrintPercent] = useState(0);
  const [printDone, setPrintDone] = useState(false);
  // "processing" trong luc dang doc/ghi CSV, "completed" tu luc file zip xong toi khi buoc gui nha
  // in tu dong hoan tat — dung de doi label progress bar ben duoi cho dung giai doan hien tai.
  const [printStatus, setPrintStatus] = useState<string>("processing");
  const [qrImagePercent, setQrImagePercent] = useState(0);
  const [qrImageDone, setQrImageDone] = useState(false);

  useJobProgress(qrGenJobId, qrGenJobId ? planId : null, (message) => {
    setQrGenPercent(Number(message.progressPercent || 0));
    const status = String(message.status || "").toLowerCase();
    if (status === "completed" || status === "failed" || status === "stalled" || status === "cancelled") {
      setQrGenJobId(null);
    }
  });

  // Xem chu thich QR_GENERATION_JOB_TERMINAL_EVENT o tren — bat huy ngay lap tuc thay vi doi
  // useJobProgress ben tren tu phat hien qua polling (co the cham vai chuc giay sau khi huy).
  useEffect(() => {
    if (!qrGenJobId) return undefined;

    const handleTerminal = () => {
      if (getStoredQrGenerationJobId(planId) !== qrGenJobId) setQrGenJobId(null);
    };

    window.addEventListener(QR_GENERATION_JOB_TERMINAL_EVENT, handleTerminal);
    return () =>
      window.removeEventListener(QR_GENERATION_JOB_TERMINAL_EVENT, handleTerminal);
  }, [qrGenJobId, planId]);

  usePrintPackageJobProgress(
    printJob && !printDone ? printJob.jobId : null,
    (message) => {
      setPrintPercent(Number(message.progressPercent || 0));
      const status = String(message.status || "").toLowerCase();
      setPrintStatus(status);
      // "completed" KHONG duoc coi la xong o day — job export CSV/zip xong khong co nghia buoc gui
      // nha in tu dong (ExportJobCard.handleSendToPrintNow) da chay xong, van phai tiep tuc hien
      // progress ("Dang gui nha in...") cho toi khi nhan PRINT_EXPORT_JOB_TERMINAL_EVENT ben duoi.
      if (status === "failed" || status === "stalled" || status === "cancelled")
        setPrintDone(true);
    },
  );

  // Bat "xong that su" (bao gom ca buoc gui nha in) qua event ExportJobCard phat sau khi
  // sendToPrintIssuancePlan thanh cong hoac job bi huy — luc nay entry trong localStorage da duoc
  // xoa (clearStoredActivePrintExportJob), nen kiem tra lai storage de chac chan dung job nay.
  useEffect(() => {
    if (!printJob || printDone) return undefined;

    const handleTerminal = () => {
      const stillActive = getStoredActivePrintExportJobs().some(
        (j) => j.jobId === printJob.jobId,
      );
      if (!stillActive) setPrintDone(true);
    };

    window.addEventListener(PRINT_EXPORT_JOB_TERMINAL_EVENT, handleTerminal);
    return () =>
      window.removeEventListener(PRINT_EXPORT_JOB_TERMINAL_EVENT, handleTerminal);
  }, [printJob, printDone]);

  useQrImageJobProgress(
    qrImageJob && !qrImageDone ? qrImageJob.jobId : null,
    (message) => {
      setQrImagePercent(Number(message.progressPercent || 0));
      const status = String(message.status || "").toLowerCase();
      if (status === "completed" || status === "failed" || status === "stalled" || status === "cancelled")
        setQrImageDone(true);
    },
  );

  // Key cua popup noi (GlobalJobProgress.tsx) tuong ung voi job dang active hien tai, theo dung
  // thu tu uu tien ben duoi (sinh vé/QR > xuat goi in > xuat anh QR) — dung de dong bo icon "eye"
  // voi dung trang thai thu gon/mo rong cua popup do.
  const activePanelKey = qrGenJobId
    ? buildJobPanelKey("qr", qrGenJobId)
    : printJob && !printDone
      ? buildJobPanelKey("export", printJob.jobId)
      : qrImageJob && !qrImageDone
        ? buildJobPanelKey("qrImageExport", qrImageJob.jobId)
        : null;

  // Mac dinh coi la dang thu gon (khop voi hanh vi mac dinh cua GlobalJobProgress — popup luon thu
  // nho thanh icon tron, nguoi dung tu bam "eye" de mo rong) — se duoc GlobalJobProgress phat lai
  // dung trang thai ngay sau khi component nay mount, qua JOB_PANEL_STATE_EVENT.
  const [panelCollapsed, setPanelCollapsed] = useState(true);

  useEffect(() => {
    if (!activePanelKey) return undefined;

    const handlePanelState = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key !== activePanelKey) return;
      setPanelCollapsed(!!detail.collapsed);
    };

    window.addEventListener(JOB_PANEL_STATE_EVENT, handlePanelState);
    return () =>
      window.removeEventListener(JOB_PANEL_STATE_EVENT, handlePanelState);
  }, [activePanelKey]);

  const handleTogglePanel = () => {
    if (!activePanelKey) return;
    window.dispatchEvent(
      new CustomEvent(JOB_PANEL_TOGGLE_REQUEST_EVENT, {
        detail: { key: activePanelKey },
      }),
    );
  };

  const renderEyeToggle = () => (
    <button
      type="button"
      className="btn btn-sm btn-light p-1 lh-1 ms-2"
      aria-label={
        panelCollapsed ? st('mo_rong_popup_tien_do_text') : st('thu_nho_popup_tien_do_text')
      }
      title={panelCollapsed ? st('mo_rong_popup_tien_do_text') : st('thu_nho_popup_tien_do_text')}
      onClick={handleTogglePanel}
    >
      <i className={panelCollapsed ? "ri-eye-line" : "ri-eye-off-line"}></i>
    </button>
  );

  if (qrGenJobId) {
    return (
      <div className="d-flex align-items-center">
        <CircularProgress percent={qrGenPercent} label={st('dang_sinh_ve_qr_label')} />
        {renderEyeToggle()}
      </div>
    );
  }
  if (printJob && !printDone) {
    return (
      <div className="d-flex align-items-center">
        <CircularProgress
          percent={printPercent}
          label={printStatus === "completed" ? st('dang_gui_nha_in_text') : st('dang_xuat_goi_in_text')}
        />
        {renderEyeToggle()}
      </div>
    );
  }
  if (qrImageJob && !qrImageDone) {
    return (
      <div className="d-flex align-items-center">
        <CircularProgress
          percent={qrImagePercent}
          label={st('dang_xuat_anh_qr_label')}
        />
        {renderEyeToggle()}
      </div>
    );
  }

  return (
    <>
      <span className={statusBadgeClassName}>{statusLabel}</span>
      {rejectionReason && (
        <small className="text-danger d-block mt-1">{rejectionReason}</small>
      )}
    </>
  );
};

export default PlanStatusColumn;
